import { lazy, Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSheet from "@/components/CartSheet";
import MobileBottomNav from "@/components/MobileBottomNav";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import WelcomePopup from "@/components/WelcomePopup";
import PersonalShopper from "@/components/PersonalShopper";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import TrustStrip from "@/components/TrustStrip";
import ScrollToTop from "@/components/ScrollToTop";
import CanonicalTag from "@/components/CanonicalTag";
import ProtectedRoute from "@/components/ProtectedRoute";
import IOSInstallModal from "@/components/IOSInstallModal";
import StickyReviewsTab from "@/components/StickyReviewsTab";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { api } from "@/lib/api";
import { useAnalytics } from "@/lib/analytics";

// Eagerly loaded — needed on first paint
import Home from "@/pages/Home";
// Footer policy pages: tiny, and must open instantly on tap (a lazy chunk made the link look dead)
import Contact from "@/pages/Contact";
import Returns from "@/pages/Returns";
import TrackOrder from "@/pages/TrackOrder";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Shipping from "@/pages/Shipping";

// Lazy-loaded — split into separate chunks loaded only when route is visited
const CategoryPage = lazy(() => import("@/pages/CategoryPage"));
const Collections = lazy(() => import("@/pages/Collections"));
const NewArrivals = lazy(() => import("@/pages/NewArrivals"));
const TypeLandingPage = lazy(() => import("@/pages/TypeLandingPage"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const SourceOnDemand = lazy(() => import("@/pages/SourceOnDemand"));
const About = lazy(() => import("@/pages/About"));
const Reviews = lazy(() => import("@/pages/Reviews"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ReferralLanding = lazy(() => import("@/pages/ReferralLanding"));
const WalletPage = lazy(() => import("@/pages/WalletPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const AccountPage = lazy(() => import("@/pages/AccountPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const AdminProducts = lazy(() => import("@/pages/AdminProducts"));
const AdminOrders = lazy(() => import("@/pages/AdminOrders"));
const AdminCustomers = lazy(() => import("@/pages/AdminCustomers"));
const AdminCategories = lazy(() => import("@/pages/AdminCategories"));
const AdminR2Stats = lazy(() => import("@/pages/AdminR2Stats"));
const AdminWhatsappOrders = lazy(() => import("@/pages/AdminWhatsappOrders"));
const AdminStaff = lazy(() => import("@/pages/AdminStaff"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminReturns = lazy(() => import("@/pages/AdminReturns"));
const AdminScrapeJobs = lazy(() => import("@/pages/AdminScrapeJobs"));
const AdminShopifyImport = lazy(() => import("@/pages/AdminShopifyImport"));
const AdminSyncToProd = lazy(() => import("@/pages/AdminSyncToProd"));
const AdminAbandonedCheckouts = lazy(() => import("@/pages/AdminAbandonedCheckouts"));
const AdminMetaPixel = lazy(() => import("@/pages/AdminMetaPixel"));
const AdminHomepage = lazy(() => import("@/pages/AdminHomepage"));
const AdminBrandAliases = lazy(() => import("@/pages/AdminBrandAliases"));
const AdminOpenaiPixel = lazy(() => import("@/pages/AdminOpenaiPixel"));
const AdminReferrals = lazy(() => import("@/pages/AdminReferrals"));
const AiStats = lazy(() => import("@/pages/AiStats"));
const WishlistShare = lazy(() => import("@/pages/WishlistShare"));
const WishlistPage = lazy(() => import("@/pages/WishlistPage"));
const BrandHub = lazy(() => import("@/pages/BrandHub"));
const CheckoutPage = lazy(() => import("@/pages/CheckoutPage"));
const OrderSuccessPage = lazy(() => import("@/pages/OrderSuccessPage"));
const UpiPayPage = lazy(() => import("@/pages/UpiPayPage"));
const AdminUpiAccounts = lazy(() => import("@/pages/AdminUpiAccounts"));
const ResumeCart = lazy(() => import("@/pages/ResumeCart"));

// Legacy WordPress /product-category/<slug> → /category/<slug>
const LegacyCategoryRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/category/${slug}`} replace />;
};

const PageSkeleton = () => (
  <div className="lux-container py-16">
    <div className="h-8 w-64 bg-neutral-100 animate-pulse mb-6" />
    <div className="h-4 w-40 bg-neutral-100 animate-pulse mb-12" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="animate-pulse">
          <div className="aspect-square bg-neutral-100" />
          <div className="h-3 bg-neutral-100 mt-3 w-2/3" />
          <div className="h-3 bg-neutral-100 mt-2 w-1/3" />
        </div>
      ))}
    </div>
  </div>
);

const Shell = ({ children, categories }) => {
  useAnalytics();
  const { pathname } = useLocation();
  // Checkout has its own full-width "Send via WhatsApp" CTA right in the
  // page flow — the floating FAB's fixed 140px-from-bottom offset (tuned to
  // clear a PDP's sticky Add-to-Bag bar) has no such bar to clear here, so
  // it landed on top of the CTA's helper text instead. A second WhatsApp
  // button is redundant on this page anyway.
  const hideWhatsAppFab = pathname.startsWith("/checkout");
  return (
    <>
      <Header categories={categories} />
      <TrustStrip />
      {/* pb-24 on mobile so the fixed MobileBottomNav (56px + safe-area inset)
          never covers the last row of content or the footer.  Without this
          the very bottom of the page appeared blank when scrolled to the end.  */}
      <main className="pb-24 md:pb-0">{children}</main>
      <Footer categories={categories} />
      <CartSheet />
      <Toaster position="top-center" duration={3000} />
      {!hideWhatsAppFab && <WhatsAppFAB />}
      <MobileBottomNav />
      <CookieConsentBanner />
      <WelcomePopup />
      <PersonalShopper />
      <IOSInstallModal />
      <StickyReviewsTab />
    </>
  );
};

function App() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api
      .categories({ per_page: 100, hide_empty: true, orderby: "count", order: "desc" })
      .then((r) => setCategories(r.items || []))
      .catch((e) => console.error("categories", e));
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
          <BrowserRouter>
            <ScrollToTop />
            <CanonicalTag />
            <Shell categories={categories}>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  <Route path="/" element={<Home categories={categories} />} />
                  <Route path="/category/womens-shoes" element={<Navigate to="/category/women-footwear?footwear_type=Pumps,Loafers,Ballet%20Flats" replace />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/c/:parentSlug/:typeSlug" element={<TypeLandingPage />} />
                  <Route path="/collections" element={<Collections />} />
                  <Route path="/collections/:gender" element={<Collections />} />
                  <Route path="/new-arrivals/:gender" element={<NewArrivals />} />
                  <Route path="/product-category/:slug" element={<LegacyCategoryRedirect />} />
                  <Route path="/shop" element={<Navigate to="/" replace />} />
                  <Route path="/product/:slug" element={<ProductPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/order/success/:txnId" element={<OrderSuccessPage />} />
                  <Route path="/pay/:orderNumber" element={<UpiPayPage />} />
                  <Route path="/source-on-demand" element={<SourceOnDemand />} />
                  <Route path="/source" element={<Navigate to="/source-on-demand" replace />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/reviews" element={<Reviews />} />
                  <Route path="/wishlist/share" element={<WishlistShare />} />
                  <Route path="/wishlist" element={<WishlistPage />} />
                  <Route path="/brand/:slug" element={<BrandHub />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/returns" element={<Returns />} />
                  <Route path="/track-order" element={<TrackOrder />} />
                  <Route path="/shipping" element={<Shipping />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/ref/:code" element={<ReferralLanding />} />
                  <Route
                    path="/wallet"
                    element={
                      <ProtectedRoute>
                        <WalletPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <AccountPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/cart/resume/:leadId"
                    element={<ResumeCart />}
                  />
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute staffOnly>
                        <AdminPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminProducts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminOrders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/customers"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminCustomers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminCategories />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/scrape-jobs"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminScrapeJobs />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/shopify-import"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminShopifyImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/sync-to-prod"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminSyncToProd />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/abandoned-checkouts"
                    element={
                      <ProtectedRoute staffOnly>
                        <AdminAbandonedCheckouts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/meta-pixel"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminMetaPixel />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/homepage"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminHomepage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/brand-aliases"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminBrandAliases />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/openai-pixel"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminOpenaiPixel />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/referrals"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminReferrals />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/upi-accounts"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminUpiAccounts />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/r2-stats"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminR2Stats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/whatsapp-orders"
                    element={
                      <ProtectedRoute staffOnly>
                        <AdminWhatsappOrders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/staff"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminStaff />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/reviews"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminReviews />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/dashboard"
                    element={
                      <ProtectedRoute adminOnly>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/returns"
                    element={
                      <ProtectedRoute staffOnly>
                        <AdminReturns />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/ai-stats"
                    element={
                      <ProtectedRoute adminOnly>
                        <AiStats />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <div className="lux-container py-32 text-center">
                        <p className="overline text-neutral-500 mb-3">404</p>
                        <h1 className="font-serif-display text-5xl">Page not found</h1>
                      </div>
                    }
                  />
                </Routes>
              </Suspense>
            </Shell>
          </BrowserRouter>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
